import { CalendarIcon, ClockIcon, DurationIcon, FlagIcon, UserIcon } from "@xola/icons";
import { formatDate } from "@xola/ui-kit";
import clsx from "clsx";
import { isExperienceProduct, Loader } from "common";
import pluralize from "pluralize";
import React from "react";
import { Purchase } from "types";
import { getCustomerName, getPurchaseProductId } from "../../models/Purchase";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { formatDuration } from "@/helpers/durationHelper";
import { useProduct } from "@/hooks/resources/useProduct";

interface PurchaseSummaryTileProps {
    purchase: Purchase;
    itemId?: ID;
    className?: string;
    showGuests?: boolean;
}

export const PurchaseSummaryTile = ({ purchase, itemId, className, showGuests = false }: PurchaseSummaryTileProps) => {
    const item = purchase.items.find((item) => item.id === itemId);

    const productId = getPurchaseProductId(purchase, itemId);

    const { data: product, isLoading: isProductLoading } = useProduct(productId, null, { enabled: !!productId });

    if (isProductLoading) {
        return <Loader />;
    }

    if (!product) {
        return null;
    }

    const arrivalDateTime = item && "arrivalDateTime" in item ? item.arrivalDateTime : null;
    const arrivalTime = item && "arrivalTime" in item ? item.arrivalTime : null;
    const guestCount = item?.quantity ?? item?.guests?.length ?? 0;
    const showDuration =
        !showGuests && isExperienceProduct(product) && !!product.eventDuration && product.eventDuration > 0;

    return (
        <div className="ui-purchase-summary-tile rounded-lg bg-gray-lighter">
            <div
                className={clsx("w-full py-1 px-3 font-bold", !showGuests && "border-b border-gray-light")}
                data-testid="refund-booking-name"
            >
                <FlagIcon size="medium" className="mr-1 mb-1 text-warning" />{" "}
                <NoTranslate as="span">{getCustomerName(purchase)}</NoTranslate>
            </div>

            <div
                className={clsx("flex items-center space-x-1 rounded-lg p-4", showGuests ? "pt-0" : "mb-10", className)}
            >
                <ProductImage size="medium" product={product} />

                <div className="flex w-full flex-col justify-between gap-1">
                    <NoTranslate as="div" className="font-bold leading-4" data-testid="refund-listing-name">
                        {product.name}
                    </NoTranslate>
                    {arrivalDateTime && (
                        <div
                            className={clsx(
                                "flex text-gray-darker",
                                showGuests ? "flex-wrap items-center gap-x-3 gap-y-1 text-sm" : "justify-between",
                            )}
                        >
                            <span className="inline-flex items-center gap-1" data-testid="refund-booking-info-date">
                                <CalendarIcon />
                                <NoTranslate>{formatDate(arrivalDateTime, "ddd LL")}</NoTranslate>
                            </span>

                            {arrivalTime && (
                                <span className="inline-flex items-center gap-1">
                                    <ClockIcon />
                                    <NoTranslate>{formatDate(arrivalDateTime, "LT")}</NoTranslate>
                                </span>
                            )}

                            {showGuests && guestCount > 0 && (
                                <span className="inline-flex items-center gap-1">
                                    <UserIcon />
                                    {guestCount} {pluralize("Guest", guestCount)}
                                </span>
                            )}
                        </div>
                    )}
                    {showDuration && product.eventDuration && (
                        <div className="text-gray-darker" data-testid="refund-booking-duration">
                            <DurationIcon className="mr-1" />
                            {formatDuration(product.eventDuration)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
